// HDR Toning — tone mapping for high dynamic range images.
// Functional approximation: local tone mapping operator that compresses
// dynamic range while preserving local contrast.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface HDRToningParams {
  method: "local-adaptation" | "exposure-gamma" | "highlight-compression";
  exposure: number; // -10..10, default 0
  gamma: number; // 0.1..3, default 1
  edgeGlow: number; // 0..2, default 0.5
  detail: number; // 0..3, default 1
  shadow: number; // -100..100, default 0
  highlight: number; // -100..100, default 0
  vibrance: number; // -100..100, default 0
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applyLocalAdaptation(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  params: HDRToningParams,
): void {
  const n = w * h;
  const { exposure, gamma, edgeGlow, detail, shadow, highlight, vibrance } = params;

  // Compute luminance
  const lum = new Float32Array(n);
  let avgLum = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    lum[i] = 0.2126 * src[o]! + 0.7152 * src[o + 1]! + 0.0722 * src[o + 2]!;
    avgLum += lum[i]!;
  }
  avgLum /= n;

  // Exposure adjustment
  const exposureFactor = Math.pow(2, exposure);
  const logAvgLum = Math.log(Math.max(avgLum / 255, 0.0001));

  // Simple box blur for local adaptation
  const radius = Math.max(1, Math.round(edgeGlow * 20));
  const blurred = new Float32Array(n);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0, count = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
            sum += lum[ny * w + nx]!;
            count++;
          }
        }
      }
      blurred[y * w + x] = sum / count;
    }
  }

  // Apply tone mapping
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const l = lum[i]! / 255;
    const local = blurred[i]! / 255;

    // Reinhard-style local tone mapping
    const mapped = (l * exposureFactor) / (1 + local * (detail + 1));

    // Shadow/highlight adjustment
    let adjusted = mapped;
    if (shadow !== 0 && mapped < 0.5) {
      adjusted += (shadow / 100) * (0.5 - mapped) * 2;
    }
    if (highlight !== 0 && mapped >= 0.5) {
      adjusted += (highlight / 100) * (mapped - 0.5) * 2;
    }

    // Gamma
    adjusted = Math.pow(Math.max(adjusted, 0), 1 / gamma);

    // Apply to RGB
    for (let c = 0; c < 3; c++) {
      let v = (src[o + c]! / 255) * adjusted / Math.max(l, 0.001);
      // Vibrance
      if (vibrance !== 0) {
        const maxC = Math.max(src[o]!, src[o + 1]!, src[o + 2]!) / 255;
        const minC = Math.min(src[o]!, src[o + 1]!, src[o + 2]!) / 255;
        const sat = maxC > 0 ? (maxC - minC) / maxC : 0;
        v = v + (v - 0.5) * (vibrance / 100) * (1 - sat);
      }
      dst[o + c] = clamp8(Math.round(v * 255));
    }
    dst[o + 3] = src[o + 3]!;
  }
}

function applyExposureGamma(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  params: HDRToningParams,
): void {
  const n = w * h;
  const { exposure, gamma, shadow, highlight, vibrance } = params;
  const exposureFactor = Math.pow(2, exposure);

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    for (let c = 0; c < 3; c++) {
      let v = (src[o + c]! / 255) * exposureFactor;
      if (shadow !== 0 && v < 0.5) {
        v += (shadow / 100) * (0.5 - v) * 2;
      }
      if (highlight !== 0 && v >= 0.5) {
        v += (highlight / 100) * (v - 0.5) * 2;
      }
      v = Math.pow(Math.max(v, 0), 1 / gamma);
      if (vibrance !== 0) {
        const maxC = Math.max(src[o]!, src[o + 1]!, src[o + 2]!) / 255;
        const minC = Math.min(src[o]!, src[o + 1]!, src[o + 2]!) / 255;
        const sat = maxC > 0 ? (maxC - minC) / maxC : 0;
        v = v + (v - 0.5) * (vibrance / 100) * (1 - sat);
      }
      dst[o + c] = clamp8(Math.round(v * 255));
    }
    dst[o + 3] = src[o + 3]!;
  }
}

function applyHighlightCompression(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  params: HDRToningParams,
): void {
  const n = w * h;
  const { exposure, gamma, shadow, highlight } = params;
  const exposureFactor = Math.pow(2, exposure);

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    for (let c = 0; c < 3; c++) {
      let v = (src[o + c]! / 255) * exposureFactor;
      // Soft highlight compression
      v = 1 - Math.exp(-v * (1 + highlight / 100));
      if (shadow !== 0 && v < 0.5) {
        v += (shadow / 100) * (0.5 - v) * 2;
      }
      v = Math.pow(Math.max(v, 0), 1 / gamma);
      dst[o + c] = clamp8(Math.round(v * 255));
    }
    dst[o + 3] = src[o + 3]!;
  }
}

export const HDRToning: Filter<HDRToningParams> = {
  id: "hdr-toning",
  displayName: "HDR Toning",
  defaultParams: {
    method: "local-adaptation",
    exposure: 0,
    gamma: 1,
    edgeGlow: 0.5,
    detail: 1,
    shadow: 0,
    highlight: 0,
    vibrance: 0,
  },

  apply(
    input: PixelBuffer,
    params: HDRToningParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.method) {
      case "local-adaptation":
        applyLocalAdaptation(src, dst, w, h, params);
        break;
      case "exposure-gamma":
        applyExposureGamma(src, dst, w, h, params);
        break;
      case "highlight-compression":
        applyHighlightCompression(src, dst, w, h, params);
        break;
    }

    return out;
  },
};
